import { ALGOLIA_ITEMS_LIMIT } from 'constants/index';
import React from 'react';
import { useLocation, useNavigate, NavigateFunction } from 'react-router-dom';
import {
    Pagination as MaestroPagination,
    Box,
    useBreakpointValue,
} from '@landr/maestro';
import { redirectIfCurrentPageIsEmpty } from 'helpers';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { css } from '@landr/styled-system/css';
import NavigationItem from './components/NavigationItem';

export const wrapper = css({
    marginTop: 'jb',
    marginBottom: 'jb',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
});

const PAGINATION_ITEMS_TO_DISPLAY = { xs: 4, md: 7, default: 7 };

const usePagination = ({
    currentPage,
    itemsPerPage,
    itemCount,
    pathname,
    search,
    navigate,
}: {
    currentPage: number;
    itemsPerPage: number;
    itemCount: number;
    pathname: string;
    search: string;
    navigate: NavigateFunction;
}) => {
    const pagesPerSlice = useBreakpointValue(PAGINATION_ITEMS_TO_DISPLAY);

    // Algolia limit result to n items, going beyond break pagination
    const totalItemsAllowed = Math.min(itemCount || 0, ALGOLIA_ITEMS_LIMIT);
    const totalPages = Math.ceil(totalItemsAllowed / itemsPerPage);

    // if we have items, make sure our currentPage have content
    !!itemCount &&
        redirectIfCurrentPageIsEmpty({
            currentPage,
            totalPages,
            pathname,
            search,
            navigate,
        });

    return {
        totalPages,
        totalItemsAllowed,
        pagesPerSlice,
    };
};

export const Pagination = ({
    itemsPerPage,
    itemCount,
    onPageChanged,
}: {
    itemsPerPage: number;
    itemCount: number;
    onPageChanged: ((nextPage: number) => void) | undefined;
}) => {
    const { page } = useCurrentSearchState();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { totalPages, totalItemsAllowed, pagesPerSlice } = usePagination({
        currentPage: page,
        itemsPerPage,
        itemCount,
        pathname,
        search,
        navigate,
    });

    const currentPage = page;

    return (
        <>
            {totalPages > 1 && (
                <Box data-component="pagination-wrapper" className={wrapper}>
                    <MaestroPagination.Root
                        onPageChanged={onPageChanged}
                        isTruncated
                        currentPage={page}
                        itemsPerPage={itemsPerPage}
                        items={totalItemsAllowed}
                        pagesPerSlice={pagesPerSlice}
                    >
                        <MaestroPagination.Previous asChild>
                            <NavigationItem page={page - 1} />
                        </MaestroPagination.Previous>
                        <MaestroPagination.Context>
                            {(context: any) =>
                                context.paginationRange.map(
                                    (page: number | 'dots') =>
                                        page === 'dots' ? (
                                            <MaestroPagination.Ellipsis
                                                key={page}
                                            />
                                        ) : (
                                            <MaestroPagination.Control
                                                key={page}
                                                page={page}
                                                asChild
                                            >
                                                <NavigationItem
                                                    page={page}
                                                    currentPage={currentPage}
                                                >
                                                    {page}
                                                </NavigationItem>
                                            </MaestroPagination.Control>
                                        ),
                                )
                            }
                        </MaestroPagination.Context>
                        <MaestroPagination.Next asChild>
                            <NavigationItem page={page + 1} />
                        </MaestroPagination.Next>
                    </MaestroPagination.Root>
                </Box>
            )}
        </>
    );
};

export default Pagination;
